import { useAppDispatch } from '@core/redux/hooks';
import {
  decreaseLoading,
  increaseLoading,
} from '@features/layout/store/layout.slice';
import {
  createContext,
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';

import { useProgram } from '../hooks/useProgram';
import {
  CreateOrUpdateProgramPayload,
  Program,
  ProgramStatus,
} from '../types/program';
import { formatConfigurationStepValues } from '../utils/steps/ConfigurationStep/formatConfigurationStepValues';
import { formatContentStepValues } from '../utils/steps/ContentStep/formatContentStepValues';
import { formatInitialStepValues } from '../utils/steps/initialStep/formatInitialStepValues';
import { formatPublishStepValues } from '../utils/steps/PublishStep/formatPublishStepValues';
import { formatVisualStepValues } from '../utils/steps/VisualStep/formatVisualStepValues';

type StepName =
  | 'initialStep'
  | 'configurationStep'
  | 'contentStep'
  | 'visualStep'
  | 'publishStep';

interface StepValues {
  isValid: boolean;
  values: any;
  formattedValues: Partial<CreateOrUpdateProgramPayload>;
}

type StepDefaultValuesMap = {
  initialStep: ReturnType<typeof formatInitialStepValues>;
  configurationStep: ReturnType<typeof formatConfigurationStepValues>;
  contentStep: ReturnType<typeof formatContentStepValues>;
  visualStep: ReturnType<typeof formatVisualStepValues>;
  publishStep: ReturnType<typeof formatPublishStepValues>;
};

type StepValuesRef = Record<StepName, StepValues>;

type ProgramStepContextType = {
  stepValuesRef: RefObject<StepValuesRef>;
  validateForms: () => number | undefined;
  getPublishedUpdatePayload: () => Partial<CreateOrUpdateProgramPayload> | null;
  getStepFormattedValues: (
    step: StepName
  ) => Partial<CreateOrUpdateProgramPayload> | null;
  getStepDefaultValues: <T extends StepName>(
    step: T
  ) => StepDefaultValuesMap[T] | null;
  program?: Program | null;
  programApiInitialValues: StepDefaultValuesMap | null;
  isPublishedProgram: boolean;
  isLoadingProgram: boolean;
  shouldShowStepErrors?: boolean;
  setShouldShowStepErrors?: (value: boolean) => void;
};

const ProgramStepContext = createContext<ProgramStepContextType | null>(null);

export const ProgramStepProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const params = useParams<{ id?: string }>();
  const programId = params.id ? Number(params.id) : undefined;
  const { program, isLoading } = useProgram(programId);

  const dispatch = useAppDispatch();
  const stepValuesRef = useRef<StepValuesRef>({} as StepValuesRef);

  const isPublishedProgram = program?.status === ProgramStatus.Active;

  const [shouldShowStepErrors, setShouldShowStepErrors] = useState<boolean>(
    () => (isPublishedProgram ? true : false)
  );

  const stepMap: Record<StepName, number> = {
    initialStep: 0,
    configurationStep: 1,
    contentStep: 2,
    visualStep: 3,
    publishStep: 4,
  };

  const validateForms = (): number | undefined => {
    const invalidSteps: number[] = [];

    for (const step in stepValuesRef.current) {
      if (stepValuesRef.current.hasOwnProperty(step)) {
        const stepName = step as StepName;

        if (!stepValuesRef.current[stepName].isValid) {
          invalidSteps.push(stepMap[stepName]);
        }
      }
    }

    return invalidSteps.length > 0 ? invalidSteps[0] : undefined;
  };

  const getPublishedUpdatePayload =
    (): Partial<CreateOrUpdateProgramPayload> => {
      return Object.values(stepValuesRef.current).reduce((acc, curr) => {
        return { ...acc, ...curr.formattedValues };
      }, {});
    };

  /**
   * Retrieves the default values for a specific step in the program workflow.
   * This method is intended to be used when the program is in "published" mode
   * and is being edited.
   *
   * The returned value represents the default values for the program formatted
   * for each step.
   *
   * @template T - The type of the step name, which must extend `StepName`.
   * @param step - The name of the step for which to retrieve the default values.
   * @returns The default values for the specified step as defined in the
   *          `StepDefaultValuesMap`, or `null` if the program is not published.
   */
  const getStepDefaultValues = <T extends StepName>(
    step: T
  ): StepDefaultValuesMap[T] | null => {
    if (!isPublishedProgram) {
      return null;
    }

    return stepValuesRef.current[step]?.values as StepDefaultValuesMap[T];
  };

  /**
   * Retrieves the formatted values for a specific step, which are used in the
   * construction of the payload sent to the API. This method ensures that the
   * values are properly formatted for the given step in the program creation
   * or update process.
   *
   * @param step - The name of the step for which the formatted values are being retrieved.
   * @returns A partial object of `CreateOrUpdateProgramPayload` containing the
   *          formatted values for the specified step, or `null` if the program
   *          is not published.
   */
  const getStepFormattedValues = (
    step: StepName
  ): Partial<CreateOrUpdateProgramPayload> | null => {
    if (!isPublishedProgram) {
      return null;
    }

    return stepValuesRef.current[step]?.formattedValues;
  };

  const programApiInitialValues: StepDefaultValuesMap | null = useMemo(() => {
    if (!program) return null;

    return {
      initialStep: formatInitialStepValues(program),
      configurationStep: formatConfigurationStepValues(program),
      contentStep: formatContentStepValues(program),
      visualStep: formatVisualStepValues(program),
      publishStep: formatPublishStepValues(program),
    };
  }, [program]);

  useEffect(() => {
    if (isLoading) {
      dispatch(increaseLoading());
    } else {
      dispatch(decreaseLoading());
    }
  }, [isLoading]);

  return (
    <ProgramStepContext.Provider
      value={{
        getPublishedUpdatePayload,
        getStepDefaultValues,
        getStepFormattedValues,
        isLoadingProgram: isLoading,
        isPublishedProgram,
        program,
        programApiInitialValues,
        setShouldShowStepErrors,
        shouldShowStepErrors,
        stepValuesRef,
        validateForms,
      }}
    >
      {children}
    </ProgramStepContext.Provider>
  );
};

export const useProgramStepContext = () => {
  const context = useContext(ProgramStepContext);
  if (!context) {
    throw new Error(
      'useProgramStepContext must be used within a FormsStepProvider'
    );
  }
  return context;
};
